import { Component, OnInit } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { OrderService } from 'src/app/services/order.service';

@Component({
    selector: 'app-status-select',
    templateUrl: './status-select.component.html',
    styleUrls: ['./status-select.component.css']
})
export class StatusSelectComponent implements ICellRendererAngularComp {
    constructor(
        private apiService: OrderService
    ) { }

    statusSelect: any = null
    evidence_1 = false
    evidence_2 = false
    departureCheckCompleted = false
    dataUser : any = {}

    public cellValue!: any
    private cellParams!: ICellRendererParams
    dataCombo: any = []
    status_text:any = ""
    data: any = []
    private retriesLeft = 10; // ~10 * 50ms = 500ms total wait

    // gets called once before the renderer is used
    agInit(params: ICellRendererParams): void {
        this.cellParams = params;
        this.cellValue = params;
        this.data = params?.data;
        this.statusSelect = Number(params?.data?.status_id ?? null);
        // Defer binding until parent data is ready
        this.tryBindFromParams();
    }

    // gets called whenever the user gets the cell to refresh
    refresh(params: ICellRendererParams) {
        this.cellParams = params;
        this.cellValue = params;
        this.statusSelect = Number(params?.data?.status_id ?? this.statusSelect);
        this.retriesLeft = 10;
        this.tryBindFromParams();
        return true; // updated
    }
    ngOnInit(): void {
    }
    changedStatus() {
        // Pass row data so parent can compute address_service targets
        this.cellValue.parent.clickStatusChanged(this.cellValue.data.id, this.dataCombo, this.cellValue.data)
    }
    // Backward compatibility with template typo
    chagedStatus() {
        this.changedStatus();
    }
    private tryBindFromParams() {
        const parent: any = (this.cellParams as any)?.parent;
        const row: any = this.cellParams?.data;

        // wait until parent and its data_combo are available
        if (!parent || !row || !parent.data_combo || parent.data_combo.length === 0) {
            if (this.retriesLeft-- > 0) {
                setTimeout(() => this.tryBindFromParams(), 50);
            }
            return;
        }

        this.dataUser = parent.dataUser || {};
        this.departureCheckCompleted = !!row.departure_check_completed;
        this.evidence_1 = row.status_id_one != null && row.status_id_one > 0 ? true : false;
        this.evidence_2 = row.status_id_two != null && row.status_id_two > 0 ? true : false;

        this.dataCombo = parent.data_combo.filter((resp: any) => {
            if (!this.evidence_1 && !this.evidence_2) {
                return resp.id == 1 || resp.id == 2 || resp.id == 3;
            } else if (this.evidence_1 && !this.evidence_2) {
                return resp.id == 1 || resp.id == 2 || resp.id == 3 || resp.id == 4 || resp.id == 8;
            } else if (this.evidence_2) {
                return resp.id == 1 || resp.id == 2 || resp.id == 3 || resp.id == 4 || resp.id == 5 || resp.id == 6 || resp.id == 8;
            }
            return false;
        });

        // Base: service status text from combo by status_id
        let serviceStatusText = '';
        parent.data_combo.filter((resp: any) => {
            if (this.cellValue.value == resp.id) {
                serviceStatusText = resp.name;
            }
        })

        // Conditional display according to business rules with address_services
        const addr = Array.isArray(row?.address_services) ? row.address_services : [];
        const serviceStatusId = Number(row?.status_id);
        const serviceIsFinal = this.isFinalized(row?.status, row?.status_id);
        if (addr.length > 0) {
            // New rule: when service status is 5, take address status; if that address is 5, take next; if all addresses finalized (5) -> service
            if (serviceStatusId === 5) {
                // pick first address whose status is NOT 5
                const targetAddr = addr.find((a: any) => {
                    const sid = Number(a?.status_id);
                    return sid !== 5;
                });
                if (targetAddr) {
                    const idx = addr.indexOf(targetAddr);
                    const letter = this.indexToLetter(idx);
                    const baseText = String(targetAddr?.status ?? serviceStatusText ?? '');
                    this.status_text = letter ? `${baseText} ${letter}` : baseText;
                    // Ensure we have the numeric id; fallback by name if missing
                    let effId: any = targetAddr?.status_id;
                    if (effId == null || effId === undefined) {
                        effId = this.resolveStatusIdByName(parent.data_combo, targetAddr?.status);
                    }
                    this.statusSelect = Number(effId ?? this.statusSelect);
                } else {
                    // All address_services are 5 (finalizado) -> show service
                    this.status_text = serviceStatusText;
                    this.statusSelect = Number(row?.status_id ?? this.statusSelect);
                }
            } else if (serviceIsFinal) {
                // Previous rule: when service is finalized, show first address not finalized; else service
                const pendingAddr = addr.find((a: any) => !this.isFinalized(a?.status, a?.status_id));
                if (pendingAddr) {
                    const idx = addr.indexOf(pendingAddr);
                    const letter = this.indexToLetter(idx);
                    const baseText = String(pendingAddr?.status ?? serviceStatusText ?? '');
                    this.status_text = letter ? `${baseText} ${letter}` : baseText;
                    // Use address_service status id as the effective status for UI rules; fallback by name
                    let effId: any = pendingAddr?.status_id;
                    if (effId == null || effId === undefined) {
                        effId = this.resolveStatusIdByName(parent.data_combo, pendingAddr?.status);
                    }
                    this.statusSelect = Number(effId ?? this.statusSelect);
                } else {
                    // All address_services finalized -> show service status
                    this.status_text = serviceStatusText;
                    this.statusSelect = Number(row?.status_id ?? this.statusSelect);
                }
            } else {
                // Service not finalized -> show service status
                this.status_text = serviceStatusText;
                this.statusSelect = Number(row?.status_id ?? this.statusSelect);
            }
        } else {
            // No address_services -> show service status
            this.status_text = serviceStatusText;
            this.statusSelect = Number(row?.status_id ?? this.statusSelect);
        }
    }

    private resolveStatusIdByName(list: any[], name: any): number | null {
        try {
            const n = String(name ?? '').toLowerCase().trim();
            if (!n) return null;
            const found = Array.isArray(list) ? list.find((s: any) => String(s?.name ?? '').toLowerCase().trim() === n) : null;
            return found ? Number(found.id) : null;
        } catch { return null; }
    }

    // Helper: determines if a given status indicates finalization
    private isFinalized(statusText?: any, statusId?: any): boolean {
        try {
            const txt = String(statusText ?? '').toLowerCase();
            if (txt.includes('finaliz')) return true; // matches "finalizado", "finalizada", etc.
        } catch { }
        try {
            const idNum = Number(statusId);
            if ([5, 6].includes(idNum)) return true; // finalized statuses per requirement
        } catch { }
        return false;
    }

    // B, C, D, E, F based on address index (0->B, 1->C, ...)
    private indexToLetter(index: number): string {
        if (typeof index !== 'number' || index < 0) return '';
        const code = 'B'.charCodeAt(0) + index; // 0 => 'B'
        const ch = String.fromCharCode(code);
        // Limit to F to match requirement
        if (ch > 'F') return 'F';
        return ch;
    }

    private validateStateStatusService(){
        if(this.data.address_services.length > 0){
            for(let i = 0; i < this.data.address_services.length; i++){
                if(this.data.address_services[i].status.toUpperCase() == 'finalizado'.toUpperCase()){
                    return {data: this.data.address_services[i], index: i};
                }
            }
            return false;
        }
        return false;
    }
}
